import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import styled from "../../../component/commonCard/style.module.css";
import KpiCard from "../../../component/commonCard/parts/KpiCard";
import BreakdownBars from "../../../component/commonCard/parts/BreakdownBars";
import RankedList from "../../../component/commonCard/parts/RankedList";
import TrendChart from "../TrendChart";
import { COPPER, TEAL, GREEN, MAGENTA } from "../../../component/commonCard/parts/theme";
import {
  getAnalyticsOverview,
  getContentAnalytics,
  getGeoAnalytics,
} from "../../../api/HttpApis";
import { showToast } from "../../../utils/Toastify/Toastify";
import ExportButtons from "../ExportButtons";

const num = (n) => (n == null ? "—" : Number(n).toLocaleString());
const pct = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}%`);
const hours = (n) => (n == null ? "—" : `${Number(n).toLocaleString()}h`);
const minutes = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}m`);

const KPI_DEFS = [
  { key: "total_views", label: "Total Views", accent: COPPER, fmt: num, hint: "Video plays in the selected range (duplicate start events from the same play are counted once)." },
  { key: "video_starts", label: "Video Starts", accent: COPPER, fmt: num, hint: "Same as Total Views — the number of plays." },
  { key: "video_completions", label: "Video Completions", accent: GREEN, fmt: num, hint: "PLAYBACK_COMPLETE events — videos watched to the end." },
  { key: "average_completion_rate", label: "Avg Completion Rate", accent: GREEN, fmt: pct, hint: "Completions ÷ plays, across all content in range." },
  { key: "engagement_rate", label: "Engagement Rate", accent: MAGENTA, fmt: pct, hint: "Share of all active users who watched at least one video." },
  { key: "active_users", label: "Active Users", accent: TEAL, fmt: num, hint: "Distinct users with any event (not just playback) in range." },
  { key: "new_users", label: "New Users", accent: COPPER, fmt: num, hint: "Active users whose very first event ever falls in this range." },
  // "Unique Viewers" and "Returning Users" cards removed per request — hidden,
  // not deleted from the backend response; kpis.unique_viewers/returning_users
  // are simply no longer rendered here.
  // These four come from tables with no platform column, so the backend can't
  // scope them by platform -- total_watch_time_hours/total_sessions/
  // average_session_duration_minutes always reflect all platforms even when a
  // platform filter is active, and average_watch_time_minutes comes back null
  // in that case rather than mixing a platform-filtered viewer count with an
  // all-platform watch-time total (see AnalyticsService._overviewKpisForRange
  // on the backend). Flagged so the UI can say so instead of showing a
  // platform-filtered-looking number that isn't.
  //
  // paid_watch_time_hours / free_watch_time_hours are intentionally not shown
  // yet: the backend's paid/free split (backend PR #91) doesn't produce two
  // non-overlapping populations, so the cards would double-count. Add them
  // back here once that split is fixed.
  { key: "total_watch_time_hours", label: "Total Watch Time", accent: GREEN, fmt: hours, hint: "Sum of counted watch seconds (WatchLedgerEntry) in range.", platformUnscoped: true },
  { key: "average_watch_time_minutes", label: "Avg Watch Time / Viewer", accent: GREEN, fmt: minutes, hint: "Total watch time ÷ unique viewers.", platformUnscoped: true },
  { key: "total_sessions", label: "Total Sessions", accent: MAGENTA, fmt: num, hint: "Streaming sessions started in range (duplicate sessions opened for the same play are counted once).", platformUnscoped: true },
  { key: "average_session_duration_minutes", label: "Avg Session Duration", accent: MAGENTA, fmt: minutes, hint: "Mean time from a session's start to its last heartbeat (or explicit stop).", platformUnscoped: true },
];


const OverviewSection = () => {
  const [searchParams] = useSearchParams();
  const contentTabParams = new URLSearchParams(searchParams);
  contentTabParams.set("tab", "Content");
  const [data, setData] = useState(null);
  const [topContent, setTopContent] = useState([]);
  const [topCountries, setTopCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  const from = searchParams.get("from") || "";
  const to = searchParams.get("to") || "";
  const compare = searchParams.get("compare") || "";
  const platform = searchParams.get("platform") || "";
  const contentType = searchParams.get("contentType") || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [overviewRes, contentRes, geoRes] = await Promise.all([
          getAnalyticsOverview({ from, to, compare, platform, contentType }),
          getContentAnalytics({ limit: 20, sort: "starts", platform, contentType, from, to }),
          getGeoAnalytics({ from, to, limit: 5, platform, contentType }),
        ]);
        if (cancelled) return;
        setData(overviewRes?.data?.data || null);
        setTopContent(contentRes?.data?.data?.items || []);
        setTopCountries(geoRes?.data?.data?.top_countries || []);
      } catch (err) {
        if (!cancelled) showToast(err?.response?.data?.message || "Failed to load overview analytics", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [from, to, compare, platform, contentType]);

  if (loading) return <div className={styled.loading}>Loading overview…</div>;

  const kpis = data?.kpis || {};
  const exportRows = KPI_DEFS.map((def) => ({
    Metric: def.label,
    Value: kpis[def.key]?.value ?? "",
  }));

  return (
    <>
      <ExportButtons rows={exportRows} filename="overview-analytics" />
      <div className={styled.kpiRow}>
        {KPI_DEFS.map((def) => {
          const kpi = kpis[def.key];
          const isUnscoped = def.platformUnscoped && platform;
          const label = isUnscoped ? `${def.label} (all platforms)` : def.label;
          const hint = isUnscoped
            ? `${def.hint} Not scoped by the platform filter — always reflects all platforms.`
            : def.hint;
          return (
            <div key={def.key} title={hint}>
              <KpiCard
                label={label}
                value={def.fmt(kpi?.value)}
                accent={def.accent}
                delta={kpi?.delta_percent}
              />
            </div>
          );
        })}
      </div>

      <TrendChart
        title="Views & Active Users · Daily"
        data={data?.growth_series}
        series={[
          { key: "views", name: "Views", color: COPPER },
          { key: "active_users", name: "Active Users", color: TEAL, fillOpacity: 0 },
        ]}
        height={260}
        emptyHint="No activity recorded in this range yet."
      />

      <div className={styled.chartsRow}>
        <RankedList
          title="Top Performing Content"
          rows={topContent.map((c) => ({ label: c.title || c.slug, value: c.starts, caption: `${c.completion_percent}% completion (${c.completes} of ${c.starts} plays)` }))}
          accent={COPPER}
          valueTooltip="Share of plays among the titles shown here — how much this title was played compared to the others in this list, not how much of it people watched."
          captionTooltip="Of everyone who started this title, the share who watched it all the way to the end."
          emptyHint="No content plays recorded in this range yet."
        />
        <div title={platform ? "Not scoped by the platform filter -- always shows every platform's views, so the bars sum to the Total Views KPI above." : undefined}>
          <BreakdownBars
            title={platform ? "Top Platforms (all platforms)" : "Top Platforms"}
            // Sourced from the backend's views_by_platform, a single grouped
            // query over this same range -- so these bars always sum to the
            // "Total Views" KPI above. (Previously sourced from an unrelated,
            // all-time device breakdown that wasn't scoped to the selected
            // range and could drift from the KPI it sat next to -- BUG-04.)
            // Deliberately not scoped by the platform filter -- see
            // AnalyticsService.viewsByPlatform.
            rows={data?.views_by_platform || []}
            nameKey="platform"
            valueKey="views"
            emptyHint="No platform data yet."
          />
        </div>
      </div>

      <div className={styled.chartsRow}>
        <RankedList
          title="Top Countries"
          rows={topCountries.map((c) => ({ label: c.country, value: c.views }))}
          accent={TEAL}
          emptyHint="No geo data yet — appears once PLAYBACK_START events carry a country."
        />
        <div className={styled.chartCard}>
          <div className={styled.chartCardHead}>
            <h3 className={styled.chartTitle}>Drill Down</h3>
          </div>
          <p className={styled.drillDownText}>
            See the <Link className={styled.drillDownLink} to={`?${contentTabParams.toString()}`}>Content</Link> tab for the full leaderboard and per-video retention curves.
          </p>
        </div>
      </div>
    </>
  );
};

export default OverviewSection;
